/**
 * Real-time ticket socket hook.
 *
 * Wraps socket.io-client so a React component can do:
 *   useTicketSocket({ orderId, token, onMessage })
 * …and it will subscribe to the `order_<id>` room, deliver each new
 * `ticket:message` event through `onMessage`, auto-reconnect on
 * disconnection, and clean up when the component unmounts.
 *
 * The socket path MUST match the backend's `socketio_path` — both sides use
 * `/api/socket.io` so the Kubernetes ingress routes the handshake through
 * the existing `/api → backend:8001` rule without extra config.
 */
import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

// One shared socket per tab is enough for the whole app — rooms isolate
// the traffic. Reusing it avoids reconnect storms when the user jumps
// between tickets.
let sharedSocket = null;
let sharedRefCount = 0;

function getOrigin() {
  const fromEnv = process.env.REACT_APP_BACKEND_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return window.location.origin;
}

function acquireSocket() {
  if (sharedSocket) { sharedRefCount += 1; return sharedSocket; }
  sharedSocket = io(getOrigin(), {
    path: '/api/socket.io',
    // Polling fallback first → upgrade to WS. Matches the Emergent
    // ingress default (which can be sticky-session-less for polling).
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 15000,
    // Prevents tab-invisible timeouts on iOS Safari where the JS timer
    // throttles below the default ping interval.
    timeout: 20000,
  });
  sharedRefCount = 1;
  return sharedSocket;
}

function releaseSocket() {
  sharedRefCount = Math.max(0, sharedRefCount - 1);
  if (sharedRefCount === 0 && sharedSocket) {
    try { sharedSocket.disconnect(); } catch (_) { /* noop */ }
    sharedSocket = null;
  }
}

/**
 * @param {object}   p
 * @param {string}   p.orderId   — required. Room to subscribe to.
 * @param {string}   p.token     — JWT used for auth on `join_room`.
 * @param {Function} p.onMessage — called with the full message doc for each
 *                                 new `ticket:message` broadcast.
 * @param {Function} [p.onRead]  — optional: called with { reader } for read receipts.
 * @param {Function} [p.onTyping]— optional: called with { sender_role, sender_name, is_typing }
 *                                 for each `ticket:typing` broadcast.
 * @returns {{ emitTyping: (isTyping: boolean) => void }}
 */
export default function useTicketSocket({ orderId, token, onMessage, onRead, onTyping } = {}) {
  const handlerRef = useRef({ onMessage, onRead, onTyping });
  handlerRef.current.onMessage = onMessage;
  handlerRef.current.onRead = onRead;
  handlerRef.current.onTyping = onTyping;
  const socketRef = useRef(null);

  useEffect(() => {
    if (!orderId || !token) return undefined;

    const socket = acquireSocket();
    socketRef.current = socket;

    // Rejoin room whenever we reconnect (handles mobile network flaps).
    const joinRoom = () => {
      socket.emit('join_room', { order_id: orderId, token }, (ack) => {
        if (!ack || !ack.ok) {
          // Non-fatal — component will fall back to REST polling if it
          // still runs its own interval. We log once per failure though.
          // eslint-disable-next-line no-console
          console.warn('[ticket-socket] join_room failed:', ack && ack.error);
        }
      });
    };

    const onConnect = () => joinRoom();
    const onTicketMessage = (payload) => {
      if (!payload || payload.order_id !== orderId) return;
      try { handlerRef.current.onMessage && handlerRef.current.onMessage(payload.message); }
      catch (_) { /* component-level error must not crash the socket */ }
    };
    const onTicketRead = (payload) => {
      if (!payload || payload.order_id !== orderId) return;
      try { handlerRef.current.onRead && handlerRef.current.onRead(payload.reader); }
      catch (_) { /* noop */ }
    };
    const onTicketTyping = (payload) => {
      if (!payload || payload.order_id !== orderId) return;
      try { handlerRef.current.onTyping && handlerRef.current.onTyping(payload); }
      catch (_) { /* noop */ }
    };

    socket.on('connect', onConnect);
    socket.on('ticket:message', onTicketMessage);
    socket.on('ticket:read', onTicketRead);
    socket.on('ticket:typing', onTicketTyping);

    // If already connected (shared socket), join immediately.
    if (socket.connected) joinRoom();

    return () => {
      socket.off('connect', onConnect);
      socket.off('ticket:message', onTicketMessage);
      socket.off('ticket:read', onTicketRead);
      socket.off('ticket:typing', onTicketTyping);
      try { socket.emit('leave_room', { order_id: orderId }); } catch (_) { /* noop */ }
      socketRef.current = null;
      releaseSocket();
    };
  }, [orderId, token]);

  // Returned helper — lets the component fire-and-forget a typing signal.
  // We intentionally don't gate this behind connection state: if the
  // socket is down the event is dropped silently and onTyping subscribers
  // will simply not see it. That's fine — typing indicators are best-effort.
  const emitTyping = (isTyping) => {
    const s = socketRef.current;
    if (!s || !orderId || !token) return;
    try { s.emit('typing', { order_id: orderId, token, is_typing: !!isTyping }); }
    catch (_) { /* noop */ }
  };

  return { emitTyping };
}
